import { api } from '../client'
import type { ApiResponse } from '../types'
import type {
  ActionsResponse,
  ActivityQuery,
  ActivityResponse,
  ApproveRegistrationBody,
  ApproveRegistrationResponse,
  CalendarSettingsResponse,
  CaseDetail,
  CreateCaseBody,
  DownloadResponse,
  EventsResponse,
  GeometryResponse,
  ListCasesQuery,
  ListCasesResponse,
  ListRegistrationsQuery,
  ListRegistrationsResponse,
  PackDto,
  PatchCaseBody,
  PlanningMe,
  PlanningSettingsDto,
  PlanningStats,
  ProcedureExportFormat,
  ProcedureExportResponse,
  ProcedureSettingsResponse,
  PutCalendarBody,
  PutPlanningSettingsBody,
  PutProcedureBody,
  RegisterRetryResponse,
  RegistrationAcceptedResponse,
  RegistrationBody,
  RegistrationStatusBody,
  RegistrationStatusResponse,
  RegistrationSummary,
  RejectRegistrationBody,
  ReportResponse,
  TransitionBody,
  TransitionResponse,
  UploadCompleteResponse,
  UploadInitBody,
  UploadInitResponse,
} from './planning-dto'

export type {
  ActionView,
  AdminArea,
  AttributeFieldSpec,
  CheckReport,
  CheckResult,
  ClassNode,
  CodeListItem,
  Finding,
  FindingCode,
  FindingSeverity,
  GuardName,
  SpatialSubmissionStatus,
  StageId,
} from './planning-core'

export type {
  ActionsResponse,
  ActivityEntry,
  ActivityQuery,
  ActivityResponse,
  ApprovalStatus,
  ApproveRegistrationBody,
  ApproveRegistrationResponse,
  ArchiveCoverageResponse,
  ArchiveDownloadResponse,
  ArchiveGapDto,
  ArchiveGapReasonDto,
  ArchivePackageDto,
  CalendarHolidayDto,
  CalendarHolidayKind,
  CalendarSettingsResponse,
  CalendarWeekday,
  CaseAsAt,
  CaseAsAtDocument,
  CaseAsAtSubmission,
  CaseAuthorityDto,
  CaseChainState,
  CaseClockDto,
  CaseClockState,
  CaseDetail,
  CaseEventDto,
  CaseExportDto,
  CaseExportStatus,
  CaseProcedureView,
  CaseRevision,
  CaseSummary,
  ConversionStatus,
  CreateCaseBody,
  CreateExportBody,
  DownloadResponse,
  ErrorBody,
  EventsResponse,
  ExportBoundaryDto,
  ExportProfileDto,
  GeometryResponse,
  JwksResponse,
  ListCasesQuery,
  ListCasesResponse,
  ListRegistrationsQuery,
  ListRegistrationsResponse,
  ListTab,
  OverlayDocumentTypeDto,
  PackDto,
  PatchCaseBody,
  PlanningCalendarDto,
  PlanningErrorCode,
  PlanningMe,
  PlanningRoleBindingsDto,
  PlanningSettingsDto,
  PlanningStats,
  ProcedureClockDto,
  ProcedureExportFormat,
  ProcedureExportResponse,
  ProcedureOverlayDto,
  ProcedureSettingsResponse,
  ProcedureStageDto,
  ProcedureTransitionDto,
  PutCalendarBody,
  PutPlanningSettingsBody,
  PutProcedureBody,
  QueueArchivesBody,
  RecallBody,
  RecallResponse,
  RegisterRetryResponse,
  RegistrationAcceptedResponse,
  RegistrationBody,
  RegistrationStatus,
  RegistrationStatusBody,
  RegistrationStatusResponse,
  RegistrationSummary,
  RejectRegistrationBody,
  ReportResponse,
  RevisionDocument,
  RevisionDocumentType,
  RotateSealKeyBody,
  SealKeyDto,
  SealKeySettingsResponse,
  SealPublicJwkDto,
  TransitionBody,
  TransitionResponse,
  UploadCompleteResponse,
  UploadedDrawingFormat,
  UploadInitBody,
  UploadInitResponse,
  UploadRejectReason,
} from './planning-dto'
export { PLANNING_ERROR_CODES } from './planning-dto'
export type * from './planning-dto-imports'

/**
 * Planning (Kebenaran Merancang) console API, Phases 1A and 1B.
 *
 * PARITY: the HTTP DTOs live in planning-dto.ts, a verbatim copy of
 * services/console/planning/src/shared/dto.ts (the source of truth, contracts section 7);
 * __tests__/planning.api.parity.spec.ts fails on drift. The section 2 types they reference are in
 * planning-core.ts. Both are re-exported above, so consumers import from planning.api alone.
 *
 * UNWRAPPING: useApiQuery unwraps { isSuccess, data } (a hook reads
 * query.data?.cases); a direct `await planningApi.listCases(...)` reads `.data.cases`.
 *
 * ERRORS: the client flattens the error body into ApiRequestError; refusals are
 * read from err.code, payload extras (guards, reason) from err.body, and named-field
 * errors from err.validationErrors (see pages/planning/lib/planning-error.ts and
 * lib/submission-form.ts). ErrorBody describes that body.
 *
 * PUBLIC: registerApplicant and registrationStatus send no session and no X-Tenant-ID
 * (RequestConfig.anonymous); the API host alone picks the tenant (contracts 1B decision 1).
 */

const casePath = (id: string): string => `/planning/cases/${encodeURIComponent(id)}`
const registrationPath = (id: string): string => `/planning/registrations/${encodeURIComponent(id)}`

export const planningApi = {
  getMe: (): Promise<ApiResponse<PlanningMe>> => api.get<PlanningMe>('/planning/me'),

  getPack: (): Promise<ApiResponse<PackDto>> => api.get<PackDto>('/planning/pack'),

  getSettings: (): Promise<ApiResponse<PlanningSettingsDto>> => api.get<PlanningSettingsDto>('/planning/settings'),

  /** Admin only. Refuses a changed register server once submissions exist (409 register_server_locked). */
  updateSettings: (body: PutPlanningSettingsBody): Promise<ApiResponse<PlanningSettingsDto>> =>
    api.put<PlanningSettingsDto>('/planning/settings', body),

  /** Admin only (Phase 2 P20): the tenant's calendar (or its pack's default) and the deadlines it does not cover. */
  getCalendar: (): Promise<ApiResponse<CalendarSettingsResponse>> =>
    api.get<CalendarSettingsResponse>('/planning/settings/calendar'),

  /** Admin only. The same calendar id needs a higher revision (409 calendar_revision_not_newer); 400 invalid_calendar lists issues. */
  putCalendar: (body: PutCalendarBody): Promise<ApiResponse<CalendarSettingsResponse>> =>
    api.put<CalendarSettingsResponse>('/planning/settings/calendar', body),

  /** The published JSON Schema of the calendar file (urn:gisify:schema:planning-calendar:1). */
  getCalendarSchema: (): Promise<ApiResponse<Record<string, unknown>>> =>
    api.get<Record<string, unknown>>('/planning/settings/calendar/schema'),

  /** Admin only (Phase 2 P21): what an overlay may change in the current procedure, and the active variant. */
  getProcedureSettings: (): Promise<ApiResponse<ProcedureSettingsResponse>> =>
    api.get<ProcedureSettingsResponse>('/planning/settings/procedure'),

  /** Admin only. Validates, stores and activates an overlay (400 invalid_overlay lists issues); null restores the base. */
  putProcedureSettings: (body: PutProcedureBody): Promise<ApiResponse<ProcedureSettingsResponse>> =>
    api.put<ProcedureSettingsResponse>('/planning/settings/procedure', body),

  /** Admin only (Phase 2 P22): the active procedure as a JSON, BPMN 2.0 or DMN 1.3 file, as text. */
  exportProcedure: (format: ProcedureExportFormat): Promise<ApiResponse<ProcedureExportResponse>> =>
    api.get<ProcedureExportResponse>('/planning/settings/procedure/export', { params: { format } }),

  listCases: (query: ListCasesQuery): Promise<ApiResponse<ListCasesResponse>> =>
    api.get<ListCasesResponse>('/planning/cases', {
      params: { tab: query.tab, q: query.q, page: query.page, pageSize: query.pageSize },
    }),

  /** Idempotent on body.requestId: a replay answers the case the first call created. */
  createCase: (body: CreateCaseBody): Promise<ApiResponse<CaseDetail>> => api.post<CaseDetail>('/planning/cases', body),

  getCase: (id: string): Promise<ApiResponse<CaseDetail>> => api.get<CaseDetail>(casePath(id)),

  /** GET /cases/{id}?asAt=<seq> (Phase 2 P6): the case rebuilt from its journal up to seq. Never act on it. */
  getCaseAsAt: (id: string, seq: number): Promise<ApiResponse<CaseDetail>> =>
    api.get<CaseDetail>(casePath(id), { params: { asAt: seq } }),

  patchCase: (id: string, body: PatchCaseBody): Promise<ApiResponse<CaseDetail>> =>
    api.patch<CaseDetail>(casePath(id), body),

  initUpload: (id: string, body: UploadInitBody): Promise<ApiResponse<UploadInitResponse>> =>
    api.post<UploadInitResponse>(`${casePath(id)}/uploads`, body),

  /** The console reads nothing from the body; the case version is re-read afterwards. */
  completeUpload: (id: string, docId: string): Promise<ApiResponse<UploadCompleteResponse>> =>
    api.post<UploadCompleteResponse>(`${casePath(id)}/uploads/${encodeURIComponent(docId)}/complete`, {}),

  /** A new requestId per user action; the same requestId replays (200, replayed: true). */
  transition: (id: string, body: TransitionBody): Promise<ApiResponse<TransitionResponse>> =>
    api.post<TransitionResponse>(`${casePath(id)}/transitions`, body),

  getActions: (id: string): Promise<ApiResponse<ActionsResponse>> =>
    api.get<ActionsResponse>(`${casePath(id)}/actions`),

  getEvents: (id: string): Promise<ApiResponse<EventsResponse>> => api.get<EventsResponse>(`${casePath(id)}/events`),

  /** `revision` omitted means the latest; pass the revision the report shows so the map matches it. */
  getGeometry: (id: string, revision?: number): Promise<ApiResponse<GeometryResponse>> =>
    api.get<GeometryResponse>(`${casePath(id)}/geometry`, { params: { revision } }),

  /** `revision` omitted means the latest; the client drops an undefined param. */
  getReport: (id: string, revision?: number): Promise<ApiResponse<ReportResponse>> =>
    api.get<ReportResponse>(`${casePath(id)}/report`, { params: { revision } }),

  /** Admin only: resets the case's dead outbox rows. The console refetches the case afterwards. */
  retryRegister: (id: string): Promise<ApiResponse<RegisterRetryResponse>> =>
    api.post<RegisterRetryResponse>(`${casePath(id)}/register/retry`, {}),

  /** A 300 s presigned GET for one revision document: JSON, never a redirect (contracts 1B decision 14). */
  getDocumentDownload: (caseId: string, docId: string): Promise<ApiResponse<DownloadResponse>> =>
    api.get<DownloadResponse>(`${casePath(caseId)}/documents/${encodeURIComponent(docId)}/download`),

  /** Officers only (403 actor_not_permitted for applicants); every tab count equals the list total. */
  getStats: (): Promise<ApiResponse<PlanningStats>> => api.get<PlanningStats>('/planning/stats'),

  /** Admins only. Newest first; pass nextCursor back as cursor for the next page. */
  getActivity: (query: ActivityQuery = {}): Promise<ApiResponse<ActivityResponse>> =>
    api.get<ActivityResponse>('/planning/activity', {
      params: { cursor: query.cursor, limit: query.limit, action: query.action },
    }),

  /** The registration queue: admins, or approver officers when allowApproverProvisioning is on (never reviewers). */
  listRegistrations: (query: ListRegistrationsQuery = {}): Promise<ApiResponse<ListRegistrationsResponse>> =>
    api.get<ListRegistrationsResponse>('/planning/registrations', {
      params: { status: query.status, page: query.page, pageSize: query.pageSize },
    }),

  /** Uses a seat. temporaryPassword is present only when invitation === 'password': show it once. */
  approveRegistration: (
    id: string,
    body: ApproveRegistrationBody = {},
  ): Promise<ApiResponse<ApproveRegistrationResponse>> =>
    api.post<ApproveRegistrationResponse>(`${registrationPath(id)}/approve`, body),

  /** Answers the decided row (contracts 1B §4); the console still refetches the queue afterwards. */
  rejectRegistration: (id: string, body: RejectRegistrationBody): Promise<ApiResponse<RegistrationSummary>> =>
    api.post<RegistrationSummary>(`${registrationPath(id)}/reject`, body),

  /** PUBLIC. The same 200 whatever the email (enumeration safety); 403 registration_closed, 429 rate_limited. */
  registerApplicant: (body: RegistrationBody): Promise<ApiResponse<RegistrationAcceptedResponse>> =>
    api.post<RegistrationAcceptedResponse>('/planning/registrations', body, { anonymous: true }),

  /** PUBLIC. The token rides in the body, never the URL; an unknown token is 404 registration_not_found. */
  registrationStatus: (body: RegistrationStatusBody): Promise<ApiResponse<RegistrationStatusResponse>> =>
    api.post<RegistrationStatusResponse>('/planning/registrations/status', body, { anonymous: true }),
}
